import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, Subscription, fromEvent, takeUntil } from 'rxjs';

import { ScriptStatusHelper } from '../../../../app/core/helpers/script-status/script-status.helper';
import { ILayoutMicrofrontend } from '../../../../app/core/models/layout/layout-microfrontend.interface';
import { AnalyticsService } from '../../../../app/core/services/analytics/analytics.service';
import { StoreService } from '../../../core/services/store/store.service';
import { ContentWrapperHelper } from '../../helpers/content-wrapper.helper';

@Component({
  selector: 'app-content-wrapper',
  templateUrl: './content-wrapper.component.html',
  styleUrls: ['./content-wrapper.component.scss']
})
export class ContentWrapperComponent implements AfterViewInit, OnInit, OnDestroy {
  data: ILayoutMicrofrontend[] = [];
  rowList: ILayoutMicrofrontend[][] = [];
  errorReference;
  loadedReference;
  currentRow = 1;
  currentCollumns = 0;
  availableCollumns: number;
  row: ILayoutMicrofrontend[] = [];
  path = window.location.pathname;
  isCambioRoute = false;
  isHome = false;

  destroy$: Subject<boolean> = new Subject<boolean>();
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;

  focusableAttributes: string[] = [
    '[tabindex="0"]',
    '[tabindex="1"]',
    'app-microfrontend-error',
    'iframe',
    'app-error',
    '[role="tablist"]',
    '[role="tab"]',
    '[role="link"]',
    'a',
    '[role="button"]',
    'button',
    'select',
    'input'
  ];

  constructor(
    private route: ActivatedRoute,
    private storeService: StoreService,
    private scriptStatusHelper: ScriptStatusHelper,
    private cdref: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private activatedRoute: ActivatedRoute
  ) {
    this.errorReference = this.scriptStatusHelper.errorReference;
    this.loadedReference = this.scriptStatusHelper.loadedReference;
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit() {
    this.parseData();
    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe(() => {});
    this.analyticsService.trackRoute(this.path);

    this.checkHomeRouteImmediate();

    // Preload do banner se for home
    if (this.isHome) {
      this.preloadBanner();
    }
  }

  checkHomeRoute(): void {
    const pathname = window.location.pathname;
    const isHomeRoute = pathname === '/' || pathname === '/home' || pathname === '';

    const isMacOS = this.isMacOperatingSystem();

    this.isHome = isHomeRoute && !isMacOS;
  }

  isMacOperatingSystem(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const platform = window.navigator.platform.toLowerCase();

    return (
      platform.includes('mac') ||
      userAgent.includes('mac') ||
      userAgent.includes('darwin') ||
      platform === 'macintel' ||
      platform === 'macppc'
    );
  }

  /**
   * Verificação imediata da rota home usando window.location
   */
  checkHomeRouteImmediate(): void {
    const pathname = window.location.pathname;
    const isHomeRoute = pathname === '/' || pathname === '/home' || pathname === '';
    const isMacOS = this.isMacOperatingSystem();
    this.isHome = isHomeRoute && !isMacOS;
  }

  /**
   * Preload da imagem do banner para carregamento mais rápido
   */
  preloadBanner(): void {
    // Cria link de preload se não existir
    if (!document.querySelector('link[href*="banner_cop.svg"]')) {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = 'assets/img/banner_cop.svg';
      preloadLink.setAttribute('fetchpriority', 'high');
      document.head.appendChild(preloadLink);
    }

    // Preload programático para garantir carregamento
    const img = new Image();
    img.src = 'assets/img/banner_cop.svg';
  }

  createAndModifyStructure() {
    this.createMicrofrontendRow();
    this.cdref.detectChanges();
    this.createMicrofrontendElements();
    this.setExtraWidgetsScripts();
  }

  ngAfterViewInit() {
    const CAMBIO_ROUTE = 'cambio';

    this.createAndModifyStructure();
    let menu: any;
    //@ts-ignore
    window.getSharedData('menu').subscribe((m: any) => (menu = m));

    this.activatedRoute.url?.pipe(takeUntil(this.destroy$)).subscribe((url: any) => {
      const hasFormalization = this.searchInMenus(menu);

      if (url[0]?.path === CAMBIO_ROUTE && hasFormalization) {
        this.isCambioRoute = true;
      }
    });
  }

  searchInMenus(menuItems: any) {
    const menu = menuItems.menu ?? menuItems;
    for (let i = 0; i < menu.length; i++) {
      if (menu[i]?.path === 'formalizacao-cambio') {
        return true;
      }

      if (menu[i]?.sub_menus && menu[i]?.sub_menus?.length > 0) {
        const foundInSubMenus = this.searchInMenus(menu[i]?.sub_menus);
        if (foundInSubMenus) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Verify if there is duplicated tags sent by API
   * Set only mfe objects with unique tags/ids to this.data
   */
  parseData() {
    for (const widget of this.route.snapshot.data.mfe) {
      if (!this.data.length) {
        this.data = [widget];
        continue;
      }

      let isDuplicated = false;

      for (const dataElement of this.data) {
        if (widget.tag_html === dataElement.tag_html) {
          isDuplicated = true;
          continue;
        }
      }

      if (!isDuplicated) {
        this.data = [...this.data, widget];
      }
    }
  }

  /**
   * Assign microfrontend information from Resolver to data variable
   * Create microfrontend script elements
   */
  createMicrofrontendElements(): void {
    this.data.forEach((microfrontend) => {
      if (microfrontend.tag_html === 'mfe-likert') {
        this.handleOpenLikert();
      }

      if (!document.getElementById(`script-${microfrontend.tag_html}`)) {
        const script = document.createElement('script');
        script.src = microfrontend.url;
        document.body.appendChild(script);
        script.id = `script-${microfrontend.tag_html}`;

        script.onload = () => {
          this.storeService.setScriptList({ [microfrontend.tag_html]: true });
          this.cdref.detectChanges();
          this.scriptStatusHelper.setLoaderStatus(microfrontend.tag_html, true);
        };

        script.onerror = () => {
          this.storeService.setScriptList({ [microfrontend.tag_html]: false });
          this.cdref.detectChanges();
          this.scriptStatusHelper.setErrorStatus(microfrontend.tag_html, true);

          const attempedRoute = document.location.pathname.toLowerCase().replace('/', '');
          this.analyticsService.trackError(`erro:mfe-nao-encontrado:${microfrontend.tag_html}:${attempedRoute}`);
        };
      } else {
        this.scriptStatusHelper.setScriptsStatus(microfrontend);
      }
      this.createMicrofrontendHTMLTag(microfrontend);
    });
  }

  /**
   * Creates row list structure to wrap mfes inside of rows
   */
  createMicrofrontendRow(): void {
    this.rowList.length = 0;
    this.row.length = 0;
    this.currentCollumns = 0;

    const collumnLimit = ContentWrapperHelper.currentBreakpoint as number;
    this.availableCollumns = collumnLimit;

    this.data.forEach((microfrontend, index) => {
      const mfeCols = parseInt(microfrontend.layout.cols, 0);
      if (this.availableCollumns > mfeCols) {
        this.row.push(microfrontend);
        this.currentCollumns = this.currentCollumns + mfeCols;
      }

      if (this.availableCollumns === mfeCols) {
        this.row.push(microfrontend);
        this.rowList.push(this.row);
        this.row = [];
        this.currentCollumns = 0;
      }

      if (this.availableCollumns < mfeCols) {
        this.rowList.push(this.row);
        this.row = [microfrontend];
        this.currentCollumns = mfeCols;
      }

      if (index + 1 === this.data.length && this.row.length) {
        this.rowList.push(this.row);
      }

      this.availableCollumns = collumnLimit - this.currentCollumns;
    });
  }

  /**
   * Creates microfrontend HTML tag and add to DOM
   * Inserts grid class on microfrontend element
   * @param microfrontend Microfrontend Information fetched from API
   */
  createMicrofrontendHTMLTag(microfrontend: ILayoutMicrofrontend): void {
    const microfrontendElement = document.createElement(microfrontend.tag_html);
    microfrontendElement.id = microfrontend.tag_html;

    const wrapper = document.getElementById(`wrapper-${microfrontend.tag_html}-${this.path}`);
    const alreadyHasMicrofrontendElement = wrapper?.querySelector(microfrontend.tag_html);

    if (wrapper && alreadyHasMicrofrontendElement === null) {
      wrapper.appendChild(microfrontendElement);
    }
  }

  /**
   * Allow the user only access the contents of the mfe card if the user presses the Enter key.
   */
  selectMfeAndBecomeItAcessible(e: KeyboardEvent) {
    const helperAcessibility = e.currentTarget as HTMLElement;
    const wrapper = helperAcessibility?.nextElementSibling;

    if (!wrapper) {
      return;
    }

    this.removeFocusableElementsInsideWrapper(wrapper);

    helperAcessibility.onclick = () => {
      this.focusableAttributes.push('[tabindex="-1"]');
      this.focusableAttributes.forEach((el: any) => {
        wrapper.querySelectorAll(el).forEach((attr) => attr.setAttribute('tabindex', '0'));
      });
    };
  }

  /**
   * Remove the focusable elements inside mfe card to
   * allow the user to navigate between mfe cards without accessing the content.
   */
  removeFocusableElementsInsideWrapper(wrapper: Element): void {
    this.focusableAttributes.forEach((el: any) => {
      wrapper
        .querySelectorAll(el)
        .forEach((attr: { setAttribute: (arg0: string, arg1: string) => any }) => attr.setAttribute('tabindex', '-1'));
    });
  }

  /**
   * Define the addition of scripts for extra widgets
   */
  setExtraWidgetsScripts() {
    this.addScriptToBody('mfe-alert');
    this.addScriptToBody('mfe-likert');
    this.addScriptToBody('it-mfe-card-noticias');
    this.addScriptToBody('mfe-dados-mercado');
  }

  /**
   * Adds scripts that are contained in the /app response
   * @param microfrontendTag declared mfe tag
   */
  addScriptToBody(microfrontendTag: string) {
    this.storeService.widgetList.subscribe((widgetList: any[]) => {
      const microfrontend = widgetList.find((el: { tag_html: any }) => el.tag_html === microfrontendTag);
      if (!microfrontend) {
        return;
      }

      const script = document.createElement('script');
      script.src = microfrontend.url;
      document.body.appendChild(script);
    });
  }

  /**
   * Handle with likert mfe at home
   */
  handleOpenLikert(): void {
    let context = '';
    let initialMessage = '';

    switch (this.path) {
      case '/ndf':
        context = 'mfe-likert-home-ndf';
        initialMessage = 'Como foi sua experiência na plataforma?';
        break;
      case '/cambio':
        context = 'mfe-likert-home-cambio';
        initialMessage = 'Como foi sua experiência na plataforma?';
        break;
      case '/posicao-consolidada':
        context = 'mfe-likert-open-posicao-consolidada';
        initialMessage = 'Como foi sua experiência com o Posição de Derivativos?';
        break;
      default:
        break;
    }

    if (!context) {
      return;
    }

    // @ts-ignore
    window['setSharedData']('configuracao-likert', {
      contexto_ativo: context,
      exibir_botao_retorno: false,
      mensagem_inicial: initialMessage,
      visualizacao_compacta: true
    });
  }
}
